import { useMemo } from 'react';

import { useClient } from 'urql';
import { fromPromise } from 'xstate';

import {
  type CancelPaymentParams,
  type CancelPaymentResponse,
} from '@sg/shared-fe-features-ordering';

import {
  SweetpassCancelPaymentDocument,
  type SweetpassCancelPaymentMutation,
  type SweetpassCancelPaymentMutationVariables,
} from './graphql/SweetpassCancelPayment.generated';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Cancels a sweetpass payment intent that is taking too long to be confirmed.
 */
export const useCancelPaymentActor = () => {
  const client = useClient();

  const cancelPayment = useMemo(
    () =>
      fromPromise<CancelPaymentResponse, CancelPaymentParams>(async ({ input }) => {
        const { paymentIntentId } = input;

        if (!paymentIntentId) throw new Error(MISSING_PAYMENT_INTENT_ID_MESSAGE);

        const response = await client
          .mutation<SweetpassCancelPaymentMutation, SweetpassCancelPaymentMutationVariables>(
            SweetpassCancelPaymentDocument,
            { input: { paymentIntentId } },
          )
          .toPromise();

        return { success: Boolean(response.data?.cancelOrderPaymentIntent.success) };
      }),
    [client],
  );

  return { cancelPayment };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const MISSING_PAYMENT_INTENT_ID_MESSAGE = 'A payment intent id is required to cancel the payment';
